import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle, Filter, Globe, Layers, Monitor, Shield, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function EuropeanPublicProcurementSoftware() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="European Public Procurement Software"
        description="Software for European public procurement on TED. Search, filter, monitor, and track opportunities across all EU countries with TED Monitor."
        path="/european-public-procurement-software"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/european-public-procurement-software" },
          { lang: "it", href: "https://tedmonitor.eu/it/software-appalti-pubblici-europei" },
          { lang: "x-default", href: "https://tedmonitor.eu/european-public-procurement-software" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              European public procurement software for EU tenders
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              The European public procurement market is large, complex, and highly competitive. Operating effectively
              requires dedicated procurement software that centralizes tender data, simplifies search, and supports
              faster decision-making.
            </p>

            <p className="text-base font-medium text-foreground">
              TED Monitor is built specifically for monitoring EU public tenders published on TED.
            </p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/eu-tender-monitoring"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                EU tender monitoring
              </Link>
              <Link
                to="/ted-procurement-alerts"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                TED procurement alerts
              </Link>
              <Link
                to="/multilingual-access"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Multilingual access (24 EU languages)
              </Link>
              <Link
                to="/compliance-and-security"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Compliance and security
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>

            <p className="mt-4 text-[11px] text-muted-foreground">
              Official source:{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                TED (Tenders Electronic Daily)
              </a>{" "}
              – Supplement to the Official Journal of the European Union.
            </p>
          </div>
        </section>

        {/* What is European public procurement software */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Monitor className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                What is European public procurement software
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              European public procurement software is a digital tool that helps you manage a structured workflow for
              identifying and tracking EU public tenders. In practical terms, it enables you to:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Access EU tenders from one centralized platform",
                "Analyze opportunities with structured data",
                "Monitor new publications and updates over time",
                "Support bid decisions with consistent criteria",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Unlike institutional portals, dedicated software improves operational efficiency and supports strategic
              market visibility.
            </p>
          </div>
        </section>

        {/* Official data from TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Official EU tender data from TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor uses official tender data from{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              , the EU reference source for public procurement notices above EU thresholds.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">This provides:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "EU-wide coverage across Member States",
                "Verifiable and authoritative procurement information",
                "Consistency with EU procedures and notice formats",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Learn more about our data source →
              </Link>
            </p>
          </div>
        </section>

        {/* Core features */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Core features for EU procurement monitoring
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Effective procurement software needs advanced capabilities to keep your monitoring precise and scalable.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">With TED Monitor you can:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Search tenders by CPV code and keywords",
                "Filter by country and procedure type",
                "Track new publications and updates",
                "Work with structured, comparable data",
                "Manage high volumes without losing focus",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm mb-4">
              All in a single platform with a workflow designed for procurement users.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/eu-tender-monitoring" className="text-primary hover:underline font-medium text-sm">
                Explore EU tender monitoring →
              </Link>
              <Link to="/ted-procurement-alerts" className="text-primary hover:underline font-medium text-sm">
                Set up automated alerts →
              </Link>
            </div>
          </div>
        </section>

        {/* Complexity */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Layers className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Designed for European market complexity
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              European public procurement comes with complexity that a general-purpose tool typically cannot handle
              well:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {[
                "Different national frameworks",
                "Multiple languages",
                "Heterogeneous formats",
                "EU thresholds and procedures",
              ].map((item) => (
                <div key={item} className="p-2 bg-background rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor is designed to abstract this complexity and provide a uniform, comparable view of the EU
              tender market.
            </p>
          </div>
        </section>

        {/* Who uses */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Who uses European public procurement software
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Procurement monitoring software is used by organizations that treat EU tenders as a stable growth channel.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Companies operating across EU countries</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Specialized consultants</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Shield className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Professional firms</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">SMEs expanding into the EU market</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              A procurement platform becomes strategic when tender discovery is systematic rather than occasional.
            </p>
          </div>
        </section>

        {/* Why choose */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Why choose TED Monitor</h2>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor stands out because it is dedicated to EU procurement workflows:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Uses only official EU tender sources",
                "Focused exclusively on the European market",
                "Provides structured and filterable data",
                "Supports faster, better-informed decisions",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">Learn more about platform trust factors:</p>

            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link to="/multilingual-access" className="text-primary hover:underline font-medium">
                Multilingual access →
              </Link>
              <Link to="/compliance-and-security" className="text-primary hover:underline font-medium">
                Compliance and security →
              </Link>
              <Link to="/eu-tender-monitoring" className="text-primary hover:underline font-medium">
                EU tender monitoring →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Start using European public procurement software</h2>

            <p className="text-base mb-6 opacity-90">
              Choose a dedicated platform for monitoring EU tenders from official TED data—structured, searchable, and
              designed for procurement work.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/eu-tender-monitoring" className="text-muted-foreground hover:text-foreground">
                EU Tender Monitoring
              </Link>
              <Link to="/ted-procurement-alerts" className="text-muted-foreground hover:text-foreground">
                Procurement Alerts
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Data Source
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
